import React, { useContext, useEffect, useRef, useState } from "react"
import { Button, Div, Text } from "atomize"
import { DateTime } from "luxon"
import { motion } from "framer-motion"
import { toast } from "react-toastify"
import Container from "../atoms/Container"
import Divider from "../atoms/Divider"
import StepSwap from "../atoms/StepSwap"
import { useBackSteps } from "../atoms/useBackClose"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import {
	canEditLesson,
	getLessonTypeTileColors,
	lessonTypeDisplayNameOf,
	lessonTypeHasCourt,
	normalizeName,
} from "../utils/func.utils"
import { editRecoveredElsewhere } from "../utils/firestore.utils"
import { playerNoteOf } from "../utils/lessonContent.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import {
	isRecoveredElsewhere,
	isSubscriptionAbsence,
	isSubstitute,
	substituteOf,
} from "../utils/substitutes.utils"
import { LessonRow, periodLabel } from "./LessonTypeLessonsModal"

const byDate = (a, b) => b.lessonStart?.toDate() - a.lessonStart?.toDate()

export const RECOVERED_ELSEWHERE_LABEL = "Recuperata con un altro maestro"

// L'allievo assente in quella lezione, col suo posto: è il posto che dice a
// chi scrive quale allievo toccare, perché c'è chi un ID non ce l'ha.
const absenceIn = (lesson, { playerID, playerName }) => {
	const name = normalizeName(playerName)
	const index = (lesson?.partecipants || []).findIndex(
		player =>
			!isSubstitute(player) &&
			isSubscriptionAbsence(player) &&
			(player.playerID ? player.playerID === playerID : name && normalizeName(player.playerName) === name),
	)
	return index === -1 ? null : { player: lesson.partecipants[index], index }
}

// Chi può segnare l'assenza: chi può segnare le presenze di quella lezione,
// cioè chi la tiene, chi la può modificare e chi ci fa da assistente. Sono
// gli stessi che le regole lasciano scrivere (`takesPartIn` in
// firestore.rules), e l'app non mostra un tasto che poi verrebbe rifiutato.
const canMarkAbsence = (coach, selectedSchool, lesson) =>
	Boolean(coach) &&
	(lesson.coachID === coach.coachID ||
		(lesson.assistantCoachIDs || []).includes?.(coach.coachID) ||
		canEditLesson(coach, selectedSchool, lesson))

// Le lezioni dietro le assenze di un allievo: quelle in cui è mancato, e
// quelle in cui ha recuperato. Toccando un recupero si apre la modifica;
// toccando un'assenza il foglio la mostra da vicino — chi c'era al suo
// posto, cosa ha scritto il maestro su di lui — e lì la si segna recuperata
// con un altro maestro, o la si rimette da recuperare.
function PlayerAbsencesModal({ closeModal, selection }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { selectedSchool, getSelectedSchool } = useContext(schoolContext)
	const { getLessonType } = useContext(sharedDataContext)

	// Quello che si è segnato da qui, lezione per lezione: il foglio parte da
	// una fotografia delle assenze, e non aspetta di essere riaperto per
	// mostrare il conto nuovo.
	const [marked, setMarked] = useState({})
	const [chosenID, setChosenID] = useState(null)
	const [saving, setSaving] = useState(false)

	// la scheda dell'allievo rilegge le sue assenze solo se qui è cambiato
	// qualcosa, quando il foglio si chiude
	const changedRef = useRef(false)
	const onChangedRef = useRef(selection?.onChanged)
	useEffect(
		() => () => {
			if (changedRef.current) onChangedRef.current?.()
		},
		[],
	)

	// il tasto indietro dal dettaglio torna all'elenco, come Indietro
	useBackSteps(chosenID ? 1 : 0, () => setChosenID(null))

	const who = { playerID: selection?.playerID, playerName: selection?.playerName }
	const absences = [...(selection?.absences ?? [])].sort(byDate)
	const recoveries = [...(selection?.recoveries ?? [])].sort(byDate)
	const isElsewhere = lesson =>
		marked[lesson.lessonID] ?? isRecoveredElsewhere(absenceIn(lesson, who)?.player)
	const elsewhere = absences.filter(isElsewhere).length
	const toRecover = Math.max(0, absences.length - recoveries.length - elsewhere)

	const courtLabelOf = lessonData => {
		if (!lessonData.court || !lessonTypeHasCourt(getLessonType(lessonData.lessonType)))
			return null
		return `Campo ${lessonData.court.courtNumber ?? lessonData.court}`
	}

	const openLesson = lessonData =>
		canEditLesson(coach, selectedSchool, lessonData)
			? () => Modal.open("editLesson", lessonData)
			: undefined

	const chosen = chosenID ? absences.find(lesson => lesson.lessonID === chosenID) : null

	const setElsewhere = async (lesson, recovered) => {
		const absence = absenceIn(lesson, who)
		if (!absence || saving) return
		setSaving(true)
		try {
			await editRecoveredElsewhere(
				lesson.lessonID,
				{ playerID: absence.player.playerID, index: absence.index },
				recovered,
			)
			changedRef.current = true
			setMarked(prev => ({ ...prev, [lesson.lessonID]: recovered }))
			setChosenID(null)
		} catch {
			toast.error("Non sono riuscito a salvare l'assenza", {
				toastId: "recovered-elsewhere-not-saved",
				position: "top-center",
			})
		} finally {
			setSaving(false)
		}
	}

	const section = (title, lessons, onClickOf, noteOf) =>
		lessons.length > 0 && (
			<>
				<Text
					textSize="caption"
					textWeight="bold"
					textColor={theme.colors.text_light}
					m={{ t: "0.75rem" }}>
					{title}
				</Text>
				{lessons.map((lesson, i) => (
					<Div key={lesson.lessonID ?? i}>
						<LessonRow
							lessonData={lesson}
							courtLabel={courtLabelOf(lesson)}
							color={getLessonTypeTileColors(getLessonType(lesson.lessonType)).bg}
							onClick={onClickOf(lesson)}
							note={noteOf?.(lesson)}
						/>
						{i < lessons.length - 1 && <Divider w="2px solid" />}
					</Div>
				))}
			</>
		)

	const closeButton = (
		<Div d="flex" justify="center" m={{ t: "1rem" }}>
			<Button
				h="3.5rem"
				p={{ x: "2rem" }}
				bg={theme.colors.gunmetal_fill}
				textColor={theme.colors.white}
				textSize="subheader"
				rounded={theme.rounded["20px"]}
				onClick={closeModal}>
				Chiudi
			</Button>
		</Div>
	)

	const list = (
		<>
			<Container>
				<Text textWeight="bold" textSize="title">
					{selection?.playerName}
				</Text>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{[
						selection?.periodText ?? periodLabel(selection?.summaryDate),
						toRecover > 0
							? `${toRecover} da recuperare`
							: absences.length > 0
								? "tutto recuperato"
								: null,
					]
						.filter(Boolean)
						.join(" · ")}
				</Text>
				{absences.length > 0 && (
					<Text
						textSize="caption"
						textColor={theme.colors.text_light}
						m={{ t: "0.25rem", b: "0.5rem" }}>
						Tocca un'assenza per i dettagli, e per segnarla se l'ha recuperata con un
						altro maestro.
					</Text>
				)}
			</Container>

			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					{section(
						absences.length === 1 ? "Assente" : "Assenze",
						absences,
						lesson => () => setChosenID(lesson.lessonID),
						lesson => (isElsewhere(lesson) ? RECOVERED_ELSEWHERE_LABEL : null),
					)}
					{section(recoveries.length === 1 ? "Recupero" : "Recuperi", recoveries, openLesson)}
				</Container>
			</Div>

			<Container>{closeButton}</Container>
		</>
	)

	return (
		<StepSwap value={chosenID ?? ""}>
			{chosen ? (
				<AbsenceDetail
					lesson={chosen}
					absence={absenceIn(chosen, who)}
					playerName={selection?.playerName}
					isElsewhere={isElsewhere(chosen)}
					canMark={canMarkAbsence(coach, selectedSchool, chosen)}
					coachName={
						chosen.coachID === coach?.coachID
							? null
							: getSelectedSchool?.()?.coaches?.[chosen.coachID]?.coachName
					}
					courtLabel={courtLabelOf(chosen)}
					courseName={lessonTypeDisplayNameOf(
						getLessonType(chosen.lessonType),
						chosen.lessonType?.toUpperCase(),
					)}
					saving={saving}
					onMark={recovered => setElsewhere(chosen, recovered)}
					onOpen={openLesson(chosen)}
					onBack={() => setChosenID(null)}
				/>
			) : (
				list
			)}
		</StepSwap>
	)
}

// Un'assenza da vicino: quando, dove, chi è venuto al suo posto e cosa ha
// scritto il maestro su di lui quel giorno — tutto già nella lezione, senza
// letture in più — e i tasti per segnarla recuperata altrove o aprire la
// lezione.
function AbsenceDetail({
	lesson,
	absence,
	playerName,
	isElsewhere,
	canMark,
	coachName,
	courtLabel,
	courseName,
	saving,
	onMark,
	onOpen,
	onBack,
}) {
	const start = lesson.lessonStart?.toDate ? DateTime.fromJSDate(lesson.lessonStart.toDate()) : null
	const end = lesson.lessonEnd?.toDate ? DateTime.fromJSDate(lesson.lessonEnd.toDate()) : null
	const substitute = absence ? substituteOf(lesson.partecipants, absence.player) : null
	const note = playerNoteOf(lesson.lessonContent, absence?.player?.playerID)

	const rows = [
		isCancelledLesson(lesson) && { label: "La lezione", value: "Annullata" },
		coachName && { label: "Lezione di", value: coachName },
		substitute?.playerName && { label: "Al suo posto", value: substitute.playerName },
		isElsewhere && {
			label: "Recupero",
			value: "Con un altro maestro",
			color: theme.colors.green,
		},
	].filter(Boolean)

	return (
		<Container>
			<Text textWeight="bold" textSize="title" textColor={theme.colors.gunmetal}>
				{start ? `Assenza di ${start.setLocale("it").toFormat("cccc d LLLL")}` : "Assenza"}
			</Text>
			<Text textSize="caption" textColor={theme.colors.text_light}>
				{[
					playerName,
					courseName,
					start && end ? `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}` : null,
					courtLabel,
				]
					.filter(Boolean)
					.join(" · ")}
			</Text>

			{(rows.length > 0 || note) && (
				<Div bg={theme.colors.surface} rounded="15px" p={{ x: "1rem", y: "0.5rem" }} m={{ t: "0.75rem" }}>
					{rows.map(row => (
						<Div
							key={row.label}
							d="flex"
							justify="space-between"
							align="baseline"
							p={{ y: "0.3rem" }}
							style={{ gap: "1rem" }}>
							<Text textColor={theme.colors.text_light}>{row.label}</Text>
							<Text
								textColor={row.color ?? theme.colors.gunmetal}
								textWeight="500"
								textAlign="right">
								{row.value}
							</Text>
						</Div>
					))}
					{note && (
						<Div p={{ y: "0.3rem" }}>
							<Text textColor={theme.colors.text_light}>Su di lui, quel giorno</Text>
							<Text textColor={theme.colors.gunmetal}>{note}</Text>
						</Div>
					)}
				</Div>
			)}

			<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.75rem" }}>
				{isElsewhere
					? "L'assenza resta scritta, ma non conta tra quelle da recuperare."
					: canMark
						? "Se l'ha recuperata con un altro maestro del circolo, segnalo qui: l'assenza resta scritta, ma non conta più tra quelle da recuperare."
						: `Se l'ha recuperata con un altro maestro, può segnarlo chi tiene la lezione${coachName ? `: ${coachName.split(" ")[0]}` : ""}.`}
			</Text>

			<Div d="flex" flexDir="column" m={{ t: "1rem" }} style={{ gap: "0.6rem" }}>
				{canMark && absence && (
					<motion.div whileTap={{ scale: 0.97 }}>
						<Button
							w="100%"
							h="3.5rem"
							bg={isElsewhere ? theme.colors.surface : theme.colors.primary}
							textColor={isElsewhere ? theme.colors.gunmetal : theme.colors.white}
							textSize="subheader"
							textWeight="600"
							rounded={theme.rounded["20px"]}
							disabled={saving}
							onClick={() => onMark(!isElsewhere)}>
							{saving
								? "Salvo..."
								: isElsewhere
									? "Rimettila da recuperare"
									: RECOVERED_ELSEWHERE_LABEL}
						</Button>
					</motion.div>
				)}
				{onOpen && (
					<motion.div whileTap={{ scale: 0.97 }}>
						<Button
							w="100%"
							h="3.5rem"
							bg={theme.colors.surface}
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							rounded={theme.rounded["20px"]}
							onClick={onOpen}>
							Apri la lezione
						</Button>
					</motion.div>
				)}
				<Button
					w="100%"
					h="2.75rem"
					bg="transparent"
					textColor={theme.colors.text_light}
					textSize="subheader"
					onClick={onBack}>
					Indietro
				</Button>
			</Div>
		</Container>
	)
}

export default PlayerAbsencesModal
